import { GenericCRUDService } from './genericCRUD';
import { UnifiedRole, UnifiedRolePermission } from './unifiedRBACService';
import api from './api';

export class UnifiedRolesCRUDService extends GenericCRUDService {
  private baseUrl = '/rbac/roles';

  constructor() {
    super({ apiEndpoint: '/rbac/roles' } as any);
  }

  // Override getAll to handle the specific response format
  async getAll(params?: { 
    search?: string; 
    orderBy?: string; 
    orderDirection?: string;
    page?: number; 
    limit?: number; 
  }): Promise<{ 
    data: UnifiedRole[]; 
    total: number; 
    totalPages: number; 
    currentPage: number;
    hasNext: boolean;
    hasPrev: boolean;
  }> {
    try {
      const response = await api.get(this.baseUrl, { params });
      // Handle the response format from the backend
      if (response.data.success) {
        const data = response.data.data || [];
        const page = params?.page || 1;
        const limit = params?.limit || 10;
        const total = data.length;
        const totalPages = Math.ceil(total / limit);
        
        return {
          data,
          total,
          totalPages,
          currentPage: page,
          hasNext: page < totalPages,
          hasPrev: page > 1
        };
      } else {
        throw new Error(response.data.message || 'Failed to fetch roles');
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  // Override getById to include permissions
  async getById(id: string): Promise<UnifiedRole | null> {
    try {
      const response = await api.get(`${this.baseUrl}/${id}`);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch role');
      }
    } catch (error) {
      console.error('Error fetching role by ID:', error);
      return null;
    }
  }

  // Get role permissions
  async getRolePermissions(roleId: string): Promise<UnifiedRolePermission[]> {
    try {
      const response = await api.get(`${this.baseUrl}/${roleId}/permissions`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching role permissions:', error);
      return [];
    }
  }

  // Save role permissions with granular updates
  async saveRolePermissions(roleId: string, permissions: Omit<UnifiedRolePermission, 'id' | 'created_at'>[]): Promise<{
    added: number;
    removed: number;
    unchanged: number;
  }> {
    try {
      const response = await api.post(`${this.baseUrl}/${roleId}/permissions`, { permissions });
      return response.data.data;
    } catch (error) {
      console.error('Error saving role permissions:', error);
      throw error;
    }
  }

  async getRolePermissionsByModule(roleId: string): Promise<{
    modules: Array<{
      id: string;
      display_name: string;
      sort_order: number;
      screens: Array<{ id: string; display_name: string; resource_id: string; permission_types: string[] }>;
    }>;
  }> {
    const response = await api.get(`${this.baseUrl}/${roleId}/permissions/by-module`);
    return response.data.data;
  }

  async saveRolePermissionsByModule(roleId: string, moduleId: string, permissionTypes: string[]): Promise<void> {
    await api.post(`${this.baseUrl}/${roleId}/permissions/by-module`, { moduleId, permissionTypes });
  }

  async saveRolePermissionsByModuleScreens(roleId: string, moduleId: string, screens: Array<{ resource_id: string; permission_types: string[] }>): Promise<void> {
    await api.put(`${this.baseUrl}/${roleId}/permissions/by-module/${moduleId}/screens`, { screens });
  }

  // Preview role permission changes
  async previewRolePermissions(roleId: string, permissions: Omit<UnifiedRolePermission, 'id' | 'created_at'>[]): Promise<{
    changes: Array<{
      resourceType: string;
      resourceId: string;
      permissionType: string;
      action: 'ADD' | 'REMOVE';
    }>;
    summary: {
      totalCurrent: number;
      totalDesired: number;
      toAdd: number;
      toRemove: number;
      unchanged: number;
    };
  }> {
    try {
      const response = await api.post(`${this.baseUrl}/${roleId}/permissions/preview`, { permissions });
      return response.data.data;
    } catch (error) {
      console.error('Error previewing role permissions:', error);
      throw error;
    }
  }

  // Override create to handle role-specific logic
  async create(data: Omit<UnifiedRole, 'id' | 'created_at' | 'updated_at'>): Promise<UnifiedRole> {
    // Clean the data to avoid circular references
    const cleanData = {
      role_name: data.role_name,
      display_name: data.display_name,
      description: data.description,
      role_type: data.role_type,
      scope_type: data.scope_type,
      tenant_id: data.tenant_id,
      created_by: data.created_by,
      is_active: data.is_active
    };
    
    const response = await api.post(this.baseUrl, cleanData);
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to create role');
    }
  }

  // Override update to handle role-specific logic
  async update(id: string, data: Partial<UnifiedRole>): Promise<UnifiedRole> {
    try {
      // Clean the data to avoid circular references
      const cleanData: any = {};
      if (data.role_name !== undefined) cleanData.role_name = data.role_name;
      if (data.display_name !== undefined) cleanData.display_name = data.display_name;
      if (data.description !== undefined) cleanData.description = data.description;
      if (data.role_type !== undefined) cleanData.role_type = data.role_type;
      if (data.scope_type !== undefined) cleanData.scope_type = data.scope_type;
      if (data.tenant_id !== undefined) cleanData.tenant_id = data.tenant_id;
      if (data.is_active !== undefined) cleanData.is_active = data.is_active;
      
      const response = await api.put(`${this.baseUrl}/${id}`, cleanData);
      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to update role');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  // Override delete to handle role-specific logic
  async delete(id: string): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  // Get roles filtered by type
  async getRolesByType(roleType: 'PLATFORM' | 'TENANT'): Promise<UnifiedRole[]> {
    try {
      const result = await this.getAll();
      return result.data.filter((role: UnifiedRole) => role.role_type === roleType);
    } catch (error) {
      console.error('Error fetching roles by type:', error);
      return [];
    }
  }

  // Get roles filtered by scope
  async getRolesByScope(scopeType: 'PLATFORM' | 'TENANT'): Promise<UnifiedRole[]> {
    try {
      const result = await this.getAll();
      return result.data.filter((role: UnifiedRole) => role.scope_type === scopeType);
    } catch (error) {
      console.error('Error fetching roles by scope:', error);
      return [];
    }
  }
}

export const unifiedRolesCRUDService = new UnifiedRolesCRUDService();
